// NOSONAR
import { useCallback } from "react";
import { sendInteraction, InteractionRequestBody } from "services/userInteractionService";
import { useProductConfig, useProductContext } from "context/ProductContext";
import { getPageInteractionDefaults } from "constants/pageInteractionDefaults";
import { ApplicationModeEnum } from "state/ApplicationStateSchema";
import { DTOPages } from "constants/pageNames";
import { useOffer } from "./useOffer";
import { useQuote } from "./useQuote";
import { useContactInformation } from "./useContactInformation";
import { toSingleLetterFromLowerCaseGender } from "utils/conversions";
import { createDate, toLocalISODateString } from "utils/age";
import { cleanPhone } from "utils/formatPhone";
import { toSpouseApplicationId } from "mappers/transformDTOSubmissionData";
import { useAppSelector } from "store/hooks";

type InteractionAddress = NonNullable<InteractionRequestBody["address"]>;

interface Options {
    interactionData?: Partial<InteractionRequestBody>;
}

/**
 * Hook to send user interactions to Merkle service when:
 * - user abandons the page (beforeunload)
 * - timeout expires
 * - flow is completed manually (final decision)
 *
 * @param pageName - Current page name (ex: "landing", "quote", "application")
 * @param options - Configuration options (timeout, etc.)
 */
export function useUserInteraction(pageName: DTOPages, options?: Options) {
    const { isGIWL } = useProductContext();
    const config = useProductConfig();
    const { offerData } = useOffer();
    const { quoteData } = useQuote();
    const { contactData } = useContactInformation();

    const sessionId = useAppSelector((state) => state.application.sessionId);
    const saveContId = useAppSelector((state) => state.application.saveContId);
    const giwlClubCode = useAppSelector((state) => state.application.landingPageData?.campaign?.clubCode);

    const interaction = config.interaction;
    const applicationMode: ApplicationModeEnum = interaction.applicationMode;

    // Get data from Redux store for DTO
    const send = useCallback(
        async (overrides: Partial<InteractionRequestBody>) => {
            try {
                const getDefaultPageData = (pageName: DTOPages): Partial<InteractionRequestBody> => {
                    const interactionMetadata = getPageInteractionDefaults(applicationMode, pageName);

                    if (interactionMetadata === undefined) {
                        throw new TypeError("Invalid disposition code");
                    }
                    const productSpecificProperties = interaction;

                    const hasAddress =
                        contactData?.contactInformation?.address &&
                        contactData?.contactInformation?.city &&
                        contactData?.contactInformation?.state &&
                        contactData?.contactInformation?.zipCode;

                    const hasBirthDate = quoteData?.dobYear && quoteData?.dobMonth && quoteData?.dobDay;
                    const quoteAddress = quoteData?.state ? ({ state: quoteData.state } as InteractionAddress) : undefined;
                    const contactAddress = hasAddress
                        ? ({
                              addressLine1: contactData?.contactInformation?.address || undefined,
                              city: contactData?.contactInformation?.city || undefined,
                              state: contactData?.contactInformation?.state || undefined,
                              zipCode: contactData?.contactInformation?.zipCode || undefined,
                          } as InteractionAddress)
                        : undefined;

                    const applicantType = quoteData?.applicantType ?? "member";
                    const appInvitationCode =
                        applicantType === "member"
                            ? offerData?.invitationCode
                            : toSpouseApplicationId(offerData?.invitationCode);

                    return {
                        // Interaction metadata basic properties
                        interactionCode: interactionMetadata.interactionCode,
                        dispositionCode: interactionMetadata.dispositionCode,
                        isFinalInteraction: interactionMetadata.isFinalInteraction,
                        interactionLevel: interactionMetadata.interactionLevel ?? 1,
                        pageId: interactionMetadata.pageId,
                        product: productSpecificProperties.product,
                        channelCode: productSpecificProperties.channelCode,
                        channelId: productSpecificProperties.channelId,
                        emailSource: productSpecificProperties.emailSource,
                        phoneSource: productSpecificProperties.phoneSource,
                        primaryClientSourceSystemId: productSpecificProperties.systemIdCode,

                        // Offer data
                        firstName: contactData?.contactInformation?.firstName ?? offerData?.firstName,
                        lastName: contactData?.contactInformation?.lastName ?? offerData?.lastName,
                        gender:
                            toSingleLetterFromLowerCaseGender(quoteData?.gender) ??
                            toSingleLetterFromLowerCaseGender(offerData?.gender) ??
                            undefined,
                        planCode: offerData?.planCode,
                        campaignCode: offerData?.campaignCode,
                        // GIWL parity: aaaMember -> CLUB_MBR_FG (default false), campaignUrl -> CAMP_URL (DTO/MLTA omit these)
                        aaaMember: isGIWL ? quoteData?.isMember ?? false : undefined,
                        campaignUrl: isGIWL ? globalThis.location.origin + globalThis.location.pathname : undefined,
                        applicationId: appInvitationCode,
                        // aaaMemberNumber: offerData?.aaaMemberNumber ?? undefined, // FUTURE: Uncomment once security / letter C hack is fixed

                        // Quote data
                        birthDate: hasBirthDate
                            ? toLocalISODateString(
                                  createDate(Number(quoteData?.dobYear), Number(quoteData?.dobMonth), Number(quoteData?.dobDay))
                              )
                            : undefined,
                        coverageAmount: quoteData?.selectedCoverageAmount,
                        nicotineUse: quoteData?.hasUsedNicotineLastYear,
                        modalPremiumAmount: quoteData?.selectedCoveragePremium,
                        paymentFrequencyMode: quoteData?.selectedCoverageAmount ? "RECURRING_MONTHLY" : undefined,
                        ...(quoteAddress ? { address: quoteAddress } : {}),

                        // Contact data
                        phone: contactData?.contactInformation?.phone
                            ? cleanPhone(contactData.contactInformation.phone)
                            : undefined,
                        phoneType: contactData?.contactInformation?.phoneType || undefined,
                        email: contactData?.contactInformation?.email || undefined,
                        ...(contactAddress ? { address: contactAddress } : {}),
                        weight: contactData?.statementOfHealth?.weight
                            ? Number(contactData?.statementOfHealth?.weight)
                            : undefined,
                        heightInTotalInches:
                            contactData?.statementOfHealth?.heightFeet && contactData?.statementOfHealth?.heightInches
                                ? Number(contactData?.statementOfHealth?.heightFeet) * 12 +
                                  Number(contactData?.statementOfHealth?.heightInches)
                                : undefined,
                        clubCode: contactData?.clubCode ?? (isGIWL ? giwlClubCode : undefined),
                        policyNumber: contactData?.policyNumber ?? undefined,
                    };
                };

                const baseData = {
                    ...getDefaultPageData(pageName),
                    ...options?.interactionData,
                };

                const interactionSessionId = sessionId ?? "";
                const interactionSaveContId = saveContId ?? undefined;

                const interactionData: InteractionRequestBody = {
                    ...baseData,
                    ...overrides,
                    sessionId: interactionSessionId,
                    saveContId: interactionSaveContId,
                    url: globalThis.location.href,
                };

                await sendInteraction(interactionData);
            } catch (error) {
                console.error("Failed to send user interaction:", error);
            }
        },
        [
            pageName,
            options?.interactionData,
            applicationMode,
            interaction,
            isGIWL,
            offerData,
            quoteData,
            contactData,
            sessionId,
            saveContId,
            giwlClubCode,
        ]
    );

    const sendUserInteraction = async (overrides?: Partial<InteractionRequestBody>) => {
        const interactionMetadata = getPageInteractionDefaults(applicationMode, pageName);
        const defaultIsFinalInteraction = interactionMetadata?.isFinalInteraction ?? false;

        await send({
            dispositionCode: interactionMetadata?.dispositionCode || "USER_INTERACTION",
            isFinalInteraction: defaultIsFinalInteraction,
            ...overrides,
        });
    };

    return { sendUserInteraction };
}
